/**
 * Rhythmz — Blank Canvas AI
 * ──────────────────────────
 * Watches the AI build a scene from a text description or dynamic word selection.
 * Step-by-step progress is visible to the user via callbacks.
 *
 * Flow:
 * 1. Clear the canvas (remove 3D objects, reset to neutral)
 * 2. Send description to edge AI → get JSON {scene, palette, params, music}
 * 3. Apply scene → apply palette → apply params → compose music
 * 4. Each step is reported via onProgress callback
 */

import type { VisualizerEngine } from '@engine/VisualizerEngine';
import type { AIAgentCoordinator } from './AIAgentCoordinator';
import { aiClient } from './client';

export interface BlankCanvasResult {
  success: boolean;
  sceneName: string;
  palette: [number, number, number][];
  description: string;
  musicGenre?: string;
}

export interface BlankCanvasCallbacks {
  onProgress: (step: string, detail: string) => void;
  onComplete: (result: BlankCanvasResult) => void;
  onError: (error: string) => void;
  onSceneLoaded?: (sceneName: string, sceneIdx: number) => void;
}

const SCENE_NAMES = [
  'CrystallineSpire', 'KaleidoscopicTorus', 'ParticleNebula', 'FractalTunnel',
  'GeometricBloom', 'LightBeams', 'PlasmaGrid', 'TheVoidV2', 'BasiliskSkull',
  'CrystalColossus', 'MechanicalBeast', 'FractalDragon', 'BioluminescentForest',
  'Cuttlefish', 'GeometricMandala',
];

/* ─── Dynamic word pools for the word selector ─── */

export const WORD_POOLS = {
  mood: ['dark', 'ethereal', 'aggressive', 'dreamy', 'cosmic', 'hypnotic', 'serene', 'chaotic', 'mystical', 'industrial'],
  color: ['crimson', 'azure', 'golden', 'violet', 'emerald', 'magenta', 'cyan', 'amber', 'neon', 'iridescent'],
  scene: ['galaxy', 'crystal', 'dragon', 'forest', 'skull', 'void', 'tunnel', 'mandala', 'beast', 'spire'],
  genre: ['techno', 'ambient', 'trance', 'dubstep', 'house', 'cinematic', 'drum&bass', 'synthwave'],
};

export class BlankCanvasAI {
  private engine: VisualizerEngine;
  private coordinator: AIAgentCoordinator;
  private callbacks: BlankCanvasCallbacks;
  private currentWords: Record<string, string> = {};
  private lockedWords: Set<string> = new Set();
  private wordCycleInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    engine: VisualizerEngine,
    coordinator: AIAgentCoordinator,
    callbacks: BlankCanvasCallbacks,
  ) {
    this.engine = engine;
    this.coordinator = coordinator;
    this.callbacks = callbacks;

    // Initialize words
    for (const pool of Object.keys(WORD_POOLS)) {
      this.currentWords[pool] = WORD_POOLS[pool as keyof typeof WORD_POOLS][0];
    }
  }

  /** Get the current dynamic words. */
  get words(): Record<string, string> {
    return { ...this.currentWords };
  }

  /** Lock a word so it stops cycling. */
  lockWord(pool: string): void {
    this.lockedWords.add(pool);
  }

  /** Unlock a word so it resumes cycling. */
  unlockWord(pool: string): void {
    this.lockedWords.delete(pool);
  }

  /** Start the dynamic word cycling. */
  startWordCycle(intervalMs = 3000): void {
    if (this.wordCycleInterval) return;
    this.wordCycleInterval = setInterval(() => {
      for (const pool of Object.keys(WORD_POOLS)) {
        if (this.lockedWords.has(pool)) continue;
        const words = WORD_POOLS[pool as keyof typeof WORD_POOLS];
        const current = this.currentWords[pool];
        const idx = words.indexOf(current);
        this.currentWords[pool] = words[(idx + 1) % words.length];
      }
      this.callbacks.onProgress('words', JSON.stringify(this.currentWords));
    }, intervalMs);
  }

  /** Stop word cycling. */
  stopWordCycle(): void {
    if (this.wordCycleInterval) {
      clearInterval(this.wordCycleInterval);
      this.wordCycleInterval = null;
    }
  }

  /** Build a description from the current dynamic words. */
  buildDescriptionFromWords(): string {
    const w = this.currentWords;
    return `${w.mood} ${w.color} ${w.scene} with ${w.genre} music`;
  }

  /**
   * Generate a scene from a text description.
   * The AI builds it step-by-step, reporting progress.
   */
  async generate(description: string): Promise<BlankCanvasResult> {
    try {
      // Step 1: Clear the canvas
      this.callbacks.onProgress('clear', 'Clearing canvas...');
      this.engine.clear3DObjects();
      this.engine.setAutoRotate(false);
      this.engine.setAudioReactive(false);

      // Step 2: Ask edge AI for a scene plan
      this.callbacks.onProgress('thinking', `AI analyzing: "${description.slice(0, 60)}..."`);
      const result = await aiClient.generateText(
        `You are a 2026 visual AI director. Given this description, choose the best scene, palette, params, and music. Respond with ONLY JSON:
{"scene": "sceneName", "palette": [[r,g,b],[r,g,b],[r,g,b]], "params": {"speed": 0.5-1.5, "intensity": 0.3-1.5, "warp": 0-1, "complexity": 0.3-0.9}, "music": {"genre": "techno", "bpm": 128, "key": "C", "scale": "minor"}, "description": "one line"}

Available scenes: ${SCENE_NAMES.join(', ')}
Available genres: techno, house, ambient, drumnbass, dubstep, trance, hiphop, cinematic, loFi, synthwave, industrial, experimental

Description: "${description}"`,
        { system: 'You are a visual AI. Return only JSON.', maxTokens: 400, temperature: 0.8 },
      );

      if (!result.success || !result.result) {
        throw new Error('AI generation failed');
      }

      // Parse the AI response — multi-strategy for the 1B model's messy output
      const plan = this.parseAIResponse(result.result);
      if (!plan.scene) {
        // Fallback: pick a random scene based on description keywords
        plan.scene = this.guessScene(description);
        plan.palette = this.randomPalette();
        plan.params = { speed: 1.0, intensity: 1.0, warp: 0.5, complexity: 0.6 };
        plan.music = { genre: 'techno', bpm: 128, key: 'A', scale: 'minor' };
        this.callbacks.onProgress('fallback', `AI parse failed — using heuristic: ${plan.scene}`);
      }

      // Step 3: Apply scene
      const sceneName = String(plan.scene ?? 'CrystallineSpire');
      this.callbacks.onProgress('scene', `Loading scene: ${sceneName}`);
      const sceneIdx = SCENE_NAMES.indexOf(sceneName);
      if (sceneIdx >= 0 && this.engine.sceneManagerAccess) {
        this.engine.sceneManagerAccess.loadScene(sceneIdx);
        // Notify the UI to highlight the scene card
        this.callbacks.onSceneLoaded?.(sceneName, sceneIdx);
      } else {
        // Scene name might be close but not exact — try fuzzy match
        const fuzzyIdx = SCENE_NAMES.findIndex(s => s.toLowerCase().includes(sceneName.toLowerCase().slice(0, 6)));
        if (fuzzyIdx >= 0 && this.engine.sceneManagerAccess) {
          this.engine.sceneManagerAccess.loadScene(fuzzyIdx);
          this.callbacks.onSceneLoaded?.(SCENE_NAMES[fuzzyIdx], fuzzyIdx);
        }
      }
      await this.delay(500);

      // Step 4: Apply palette
      const palette = plan.palette as number[][] | undefined;
      this.callbacks.onProgress('palette', `Applying colors: ${palette?.map((c) => `rgb(${c[0]},${c[1]},${c[2]})`).join(', ') ?? 'default'}`);
      if (palette && Array.isArray(palette)) {
        this.engine.addPreset(`AI: ${description.slice(0, 30)}`, {
          palette: palette.map((c) => ({ r: c[0], g: c[1], b: c[2] })),
          params: (plan.params as Record<string, number>) ?? {},
        });
      }
      await this.delay(300);

      // Step 5: Apply params
      const params = plan.params as Record<string, number> | undefined;
      this.callbacks.onProgress('params', `Tuning: speed=${params?.speed}, intensity=${params?.intensity}`);
      if (params) {
        if (params.speed) this.engine.setSpeed(params.speed);
        if (params.intensity) this.engine.setIntensity(params.intensity);
        if (params.warp) this.engine.setWarp(params.warp);
        if (params.complexity) this.engine.setComplexity(params.complexity);
      }
      await this.delay(300);

      // Step 6: Compose music
      const music = plan.music as { genre?: string; bpm?: number; key?: string; scale?: string } | undefined;
      if (music) {
        this.callbacks.onProgress('music', `Composing ${music.genre} at ${music.bpm}bpm`);
        await this.coordinator.musicAI.composeFromPrompt(
          `${music.genre} in ${music.key} ${music.scale} at ${music.bpm}bpm`,
        );
      }

      // Step 7: Re-enable audio reactivity so visuals respond to the AI's music
      this.engine.setAudioReactive(true);

      const finalResult: BlankCanvasResult = {
        success: true,
        sceneName,
        palette: (palette as [number, number, number][]) ?? [[0.5, 0.5, 0.5], [0.3, 0.3, 0.3], [0.7, 0.7, 0.7]],
        description: String(plan.description ?? description),
        musicGenre: music?.genre,
      };

      this.callbacks.onProgress('complete', `Scene built: ${sceneName}`);
      this.callbacks.onComplete(finalResult);
      return finalResult;

    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      this.callbacks.onError(msg);
      // Re-enable audio reactivity even on error
      this.engine.setAudioReactive(true);
      return { success: false, sceneName: 'unknown', palette: [[0.5, 0.5, 0.5]], description: msg };
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
  }

  /** Multi-strategy JSON parser for messy LLM output. */
  private parseAIResponse(raw: string): Record<string, unknown> {
    let plan: Record<string, unknown> = {};

    // Strategy 0: strip markdown code fences
    let text = raw.replace(/```(?:json)?\s*/gi, '').replace(/```\s*/g, '').trim();

    // Strategy 1: find a balanced JSON object starting from "scene"
    const sceneStart = text.indexOf('"scene"');
    if (sceneStart > 0) {
      // Find the enclosing object by walking backwards to the opening brace
      let braceStart = text.lastIndexOf('{', sceneStart);
      if (braceStart >= 0) {
        // Walk forward to find the matching closing brace
        let depth = 0;
        let braceEnd = -1;
        for (let i = braceStart; i < text.length; i++) {
          if (text[i] === '{') depth++;
          else if (text[i] === '}') { depth--; if (depth === 0) { braceEnd = i; break; } }
        }
        if (braceEnd > braceStart) {
          const jsonStr = text.slice(braceStart, braceEnd + 1);
          try { plan = JSON.parse(jsonStr); } catch { /* fall through */ }
        }
      }
    }

    // Strategy 2: full text JSON parse (if the whole thing is valid JSON)
    if (!plan.scene) {
      try { plan = JSON.parse(text); } catch { /* fall through */ }
    }

    // Strategy 3: field-by-field regex extraction
    if (!plan.scene) {
      const sceneMatch = text.match(/"scene"\s*:\s*"([^"]+)"/);
      if (sceneMatch) plan.scene = sceneMatch[1];
    }
    if (!plan.palette) {
      const colors = text.match(/\[([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)\]/g);
      if (colors && colors.length >= 2) {
        plan.palette = colors.slice(0, 3).map((c) => {
          const m = c.match(/([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/);
          return m ? [parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3])] : [0.5, 0.5, 0.5];
        });
      }
    }
    if (!plan.music) {
      const genreMatch = text.match(/"genre"\s*:\s*"([^"]+)"/);
      if (genreMatch) {
        const bpmMatch = text.match(/"bpm"\s*:\s*(\d+)/);
        const keyMatch = text.match(/"key"\s*:\s*"([^"]+)"/);
        const scaleMatch = text.match(/"scale"\s*:\s*"([^"]+)"/);
        plan.music = {
          genre: genreMatch[1],
          bpm: bpmMatch ? parseInt(bpmMatch[1]) : 128,
          key: keyMatch ? keyMatch[1] : 'A',
          scale: scaleMatch ? scaleMatch[1] : 'minor',
        };
      }
    }

    return plan;
  }

  /** Guess a scene based on description keywords. */
  private guessScene(desc: string): string {
    const d = desc.toLowerCase();
    if (d.includes('dragon') || d.includes('serpent')) return 'FractalDragon';
    if (d.includes('skull') || d.includes('demon') || d.includes('dark')) return 'BasiliskSkull';
    if (d.includes('crystal') || d.includes('gem') || d.includes('spire')) return 'CrystalColossus';
    if (d.includes('beast') || d.includes('mech') || d.includes('machine')) return 'MechanicalBeast';
    if (d.includes('forest') || d.includes('mushroom') || d.includes('glow')) return 'BioluminescentForest';
    if (d.includes('ocean') || d.includes('sea') || d.includes('fish') || d.includes('tentacle')) return 'Cuttlefish';
    if (d.includes('mandala') || d.includes('sacred') || d.includes('geometric')) return 'GeometricMandala';
    if (d.includes('void') || d.includes('space') || d.includes('tunnel')) return 'TheVoidV2';
    if (d.includes('cosmic') || d.includes('galaxy') || d.includes('star')) return 'ParticleNebula';
    return SCENE_NAMES[Math.floor(Math.random() * SCENE_NAMES.length)];
  }

  /** Generate a random harmonious palette. */
  private randomPalette(): number[][] {
    const hue = Math.random();
    const c1 = this.hslToRgb(hue, 0.8, 0.5);
    const c2 = this.hslToRgb((hue + 0.15) % 1, 0.7, 0.4);
    const c3 = this.hslToRgb((hue + 0.3) % 1, 0.9, 0.6);
    return [c1, c2, c3];
  }

  private hslToRgb(h: number, s: number, l: number): [number, number, number] {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1; if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return [hue2rgb(p, q, h + 1/3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1/3)];
  }

  dispose(): void {
    this.stopWordCycle();
  }
}
